import { inject, Injectable } from '@angular/core';

import { AnalyticsBase, GlobalAnalyticsContext } from '@core/analytics-legacy';

export type AssistantOpenTrigger = 'floatingButton' | 'proactiveBanner';
export type AssistantNewConversationTrigger = 'windowHeader' | 'hamburgerMenu';
export type AssistantWindowMode = 'fullscreen' | 'small';
export type AssistantFeedbackType = 'positive' | 'negative';

@Injectable({ providedIn: 'root' })
export class TellentAssistantAnalyticsService extends AnalyticsBase {
  private readonly globalContext = inject(GlobalAnalyticsContext);

  public readonly namespace = 'TellentAssistant';
  public readonly requireViewContext = false;

  public windowOpened(trigger: AssistantOpenTrigger): void {
    this.track('WindowOpened', { trigger, context: this.globalContext.getFlatContext() });
  }

  public starterClicked(starter: string): void {
    this.track('StarterClicked', { starter });
  }

  // Manual messages only — starter sends excluded at call site, content never tracked.
  public messageSent(messageLength: number, threadLength: number): void {
    this.track('MessageSent', { messageLength, threadLength });
  }

  public newConversationClicked(trigger: AssistantNewConversationTrigger): void {
    this.track('NewConversationClicked', { trigger });
  }

  public hamburgerMenuOpened(): void {
    this.track('HamburgerMenuOpened');
  }

  public windowClosed(): void {
    this.track('WindowClosed');
  }

  public windowMinimized(): void {
    this.track('WindowMinimized');
  }

  public fullscreenModeChanged(targetMode: AssistantWindowMode): void {
    this.track('FullscreenModeChanged', { targetMode });
  }

  public conversationLoadedFromHistory(): void {
    this.track('ConversationLoadedFromHistory');
  }

  public widgetCardClicked(widgetType: string): void {
    this.track('WidgetCardClicked', { widgetType });
  }

  public copyToClipboardClicked(): void {
    this.track('CopyToClipboardClicked');
  }

  public feedbackThumbClicked(feedbackType: AssistantFeedbackType): void {
    this.track('FeedbackThumbClicked', { feedbackType });
  }

  public windowDragged(): void {
    this.track('WindowDragged');
  }

  public proactiveBannerViewed(suggestion: string): void {
    this.track('ProactiveBannerViewed', {
      suggestion,
      context: this.globalContext.getFlatContext(),
    });
  }

  public proactiveBannerClicked(suggestion: string): void {
    this.track('ProactiveBannerClicked', { suggestion });
  }

  public textInputFocused(): void {
    this.track('TextInputFocused');
  }

  public skillUsed(skillName: string): void {
    this.track('SkillUsed', { skillName });
  }

  public toolUsed(toolName: string): void {
    this.track('ToolUsed', { toolName });
  }
}
